import type { ChatMessage } from '../ChatWidget.svelte';
import { getApiBase } from './config';
const ALLOWED_CHAT_MODES = ['assistant', 'chat'] as const;

export type ChatMode = (typeof ALLOWED_CHAT_MODES)[number];

function parseChatMode(value: unknown): ChatMode | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toLowerCase();
  return ALLOWED_CHAT_MODES.find((mode) => mode === trimmed) ?? undefined;
}

const DEFAULT_CHAT_MODE = parseChatMode(import.meta.env.VITE_CHAT_MODE) ?? 'assistant';
let currentChatMode: ChatMode = DEFAULT_CHAT_MODE;

export function setChatMode(mode: ChatMode) {
  currentChatMode = mode;
}

export type ChatCompletionRequest = {
  messages: ChatMessage[];
  mode?: ChatMode;
};

export type ChatCompletionResponse = {
  message: ChatMessage;
  mode?: ChatMode;
  maxMessagesReached?: boolean;
};

type ChatRequestOptions = {
  token?: string;
  analyticsSessionId?: string;
};

export async function requestAssistantResponse(
  payload: ChatCompletionRequest,
  options: ChatRequestOptions = {}
): Promise<ChatCompletionResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
  }
  if (typeof options.analyticsSessionId === 'string' && options.analyticsSessionId.trim().length > 0) {
    headers['X-Analytics-Session'] = options.analyticsSessionId.trim();
  }

  const body: ChatCompletionRequest = {
    ...payload,
    mode: payload.mode ?? currentChatMode
  };

  const response = await fetch(`${getApiBase()}/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    console.error('[chat] request failed', response.status, response.statusText);
    const text = await response.text();
    throw new Error(`Chat request failed (${response.status}): ${text || response.statusText}`);
  }

  const data = (await response.json()) as ChatCompletionResponse;
  if (!data?.message?.text) {
    console.error('[chat] response missing message text', data);
    throw new Error('Chat response payload missing message text.');
  }

  console.info('[chat] assistant message received', {
    id: data.message.id,
    mode: data.mode,
    maxMessagesReached: data.maxMessagesReached
  });
  return data;
}
